import {
  RequestBasePostUserInvitationLink,
  RequestBasePostUserTermination,
  ResponseBaseGetUsersInvitationLinks,
  ResponseBasePostUserInvitationLink,
  UserCustomAttribute,
  UserInvitationLink,
  UserTermination,
} from '@hefe/models';
import {
  PayloadAction,
  createAsyncThunk,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import API from '../../../api/api';
import {
  UserUpdateChildrenRequest,
  UserUpdateRightsRequest,
} from '../../../models/requests/user';

const FEATURE_KEY = 'users';

/************************************************************************************
 *
 *                                  ACTIONS
 *
 ************************************************************************************
 */
export const fetchAllCustomUserAttributes = createAsyncThunk(
  'users/getAllCustomUserAttributes',
  async (thunkAPI) => {
    const response = await API.clubCustomUserAttributes().getAll();
    return response?.data;
  }
);

export const fetchInvitationLinks = createAsyncThunk(
  'users/getInvitationLinks',
  async (thunkAPI) => {
    const response = await API.members().getInvitationLinks();
    return response?.data;
  }
);

export const deleteMultiUsers = createAsyncThunk<any, number[]>(
  'users/deleteMulti',
  async (data: number[], thunkAPI) => {
    const response = await API.members().deleteMulti(data);
    if (response) {
      return thunkAPI.fulfillWithValue(data);
    } else {
      return thunkAPI.rejectWithValue(null);
    }
  }
);
export const activateUsers = createAsyncThunk<any, number[]>(
  'users/activate',
  async (data: number[], thunkAPI) => {
    const response = await API.members().activateMembers(data);
    if (response) {
      return thunkAPI.fulfillWithValue(data);
    } else {
      return thunkAPI.rejectWithValue(null);
    }
  }
);
export const usersSendWelcomeMail = createAsyncThunk<any, number[]>(
  'users/sendWelcomeMail',
  async (data: number[], thunkAPI) => {
    const response = await API.members().sendWelcomeMails(data);
    if (response) {
      return thunkAPI.fulfillWithValue(data);
    } else {
      return thunkAPI.rejectWithValue(null);
    }
  }
);
export const inviteUsers = createAsyncThunk<any, string[]>(
  'users/invite',
  async (data: string[], thunkAPI) => {
    const response = await API.members().inviteMember(data);
    if (response) {
      return thunkAPI.fulfillWithValue(data);
    } else {
      return thunkAPI.rejectWithValue(null);
    }
  }
);

export const generateInvitationLink = createAsyncThunk(
  'users/invite/link',
  async (request: RequestBasePostUserInvitationLink, thunkAPI) => {
    const response = await API.members().generateInvitationLink(request);
    return response?.data as ResponseBasePostUserInvitationLink;
  }
);

export const deleteInvitationLink = createAsyncThunk<any, UserInvitationLink>(
  'users/invite/link/delete',
  async (link: UserInvitationLink, thunkAPI) => {
    const response = await API.members().deleteInvitationLink(link);
    if (response) {
      return thunkAPI.fulfillWithValue(link);
    } else {
      return thunkAPI.rejectWithValue(null);
    }
  }
);

export const updateUserRights = createAsyncThunk(
  'users/updateUserRights',
  async (data: UserUpdateRightsRequest, thunkAPI) => {
    const response = await API.members().updateUserRights(data);
    return response?.data;
  }
);

export const updateUserChildren = createAsyncThunk(
  'users/updateUserChildren',
  async (data: UserUpdateChildrenRequest, thunkAPI) => {
    const response = await API.members().updateChildren(data);
    return response?.data;
  }
);

export const createUserTermination = createAsyncThunk(
  'users/createTermination',
  async (data: RequestBasePostUserTermination, thunkAPI) => {
    const response = await API.members().createTermination(data);
    return response?.data;
  }
);

export const deleteUserTermination = createAsyncThunk(
  'users/deleteTermination',
  async (data: UserTermination, thunkAPI) => {
    const response = await API.members().deleteTermination(data);
    if (response) {
      return thunkAPI.fulfillWithValue(data);
    } else {
      return thunkAPI.rejectWithValue(null);
    }
  }
);

/************************************************************************************
 *
 *                                      SLICE
 *
 ************************************************************************************
 */

export interface UsersState {
  customUserAttributes: UserCustomAttribute[];
  invitationLinks: UserInvitationLink[];
}

const initialState: UsersState = {
  customUserAttributes: [],
  invitationLinks: [],
};

export const usersSlice = createSlice({
  name: FEATURE_KEY,
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(
      fetchAllCustomUserAttributes.fulfilled,
      (state: UsersState, action: PayloadAction<UserCustomAttribute[]>) => {
        state.customUserAttributes = action.payload;
      }
    );
    builder.addCase(
      fetchInvitationLinks.fulfilled,
      (
        state: UsersState,
        action: PayloadAction<ResponseBaseGetUsersInvitationLinks>
      ) => {
        state.invitationLinks = action.payload;
      }
    );
    builder.addCase(
      generateInvitationLink.fulfilled,
      (
        state: UsersState,
        action: PayloadAction<ResponseBasePostUserInvitationLink>
      ) => {
        state.invitationLinks.push(action.payload);
      }
    );
    builder.addCase(
      deleteInvitationLink.fulfilled,
      (state: UsersState, action: PayloadAction<UserInvitationLink>) => {
        state.invitationLinks = state.invitationLinks.filter(
          (link) => link.id !== action.payload.id
        );
      }
    );
  },
});

/*
 * Export reducer for store configuration.
 */
export const usersReducer = usersSlice.reducer;
export const usersActions = usersSlice.actions;

const state = (rootState: any): UsersState => rootState[FEATURE_KEY];

export const usersCustomUserAttributesSelector = createSelector(
  state,
  (state) => {
    return state.customUserAttributes;
  }
);

export const usersInvitationLinksSelector = createSelector(state, (state) => {
  return state.invitationLinks;
});
